import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Config from 'config/config';
import { assign, each, isArray, isObject, isString } from 'lodash';
import { Observable, from, lastValueFrom } from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';

import { Toast } from '../interfaces/toast';
import { ReLoginService } from '../services/re-login.service';
import { ToastService } from '../services/toast.service';
import { SSO_SIGN_IN_REQUIRED_ERROR_NAME } from '../services/vive-api-errors';

@Injectable()
export class HttpInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private reLoginService: ReLoginService,
    private router: Router,
    private ToastService: ToastService,
    private loadingService: LoadingService
  ) {}

  recursivelyParseDateFromJsonResponseBody(data: any) {
    if (data) {
      if (
        isString(data) &&
        /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/.test(
          data
        )
      ) {
        return true;
      }

      if (isArray(data)) {
        each(data, (value: any, key: string) => {
          if (this.recursivelyParseDateFromJsonResponseBody(value)) {
            assign(data, { [key]: new Date(value) || value });
          }
        });
      }

      if (isObject(data) && !isArray(data)) {
        each(data, (value: any, key: string) => {
          if (this.recursivelyParseDateFromJsonResponseBody(value)) {
            assign(data, { [key]: new Date(value) || value });
          }
        });
      }
    }

    return false;
  }

  decodeArrayBufferError(error: ArrayBuffer): unknown {
    const errorBuffer = new Uint8Array(error);
    const stringParts: string[] = [];
    errorBuffer.forEach((charCode) => {
      stringParts.push(String.fromCharCode(charCode));
    });

    try {
      return JSON.parse(stringParts.join(''));
    } catch {
      // Body was not JSON — keep the original error on the rejection.
      return error;
    }
  }

  buildErrorToastBody(error: unknown): string {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object') {
      const { message } = error as { message?: unknown };

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }

    return 'An Error has occurred.';
  }

  // API requests go to the API host carrying the session cookie. A 401 from one
  // means the session lapsed: sign in again through the LoginModal, retry once.
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // The API is cross-origin, so the browser attaches the session cookie only
    // to requests that opt in — and only API requests should.
    const isApiRequest = /^\/(api|auth)(\/|$)/.test(request.url);
    if (isApiRequest) {
      request = request.clone({
        url: `${Config.getEnvironmentConfig().viveApiUrl}${request.url}`,
        withCredentials: true
      });
    }

    // May want to convert to a map in the future. For now, set works for error handling purposes.
    const RequestContextProperties = new Set<string>();

    const { context } = request;

    Array.from(context.keys()).forEach((token) => {
      const tokenValue = token.defaultValue();
      if (typeof tokenValue === 'string') {
        RequestContextProperties.add(tokenValue);
      }
    });

    if (!RequestContextProperties.has('SkipLoadingMask')) {
      this.loadingService.show();
    }

    const handleValidation = async (): Promise<HttpEvent<any>> => {
      try {
        return await lastValueFrom(next.handle(request));
      } catch (errorResponse) {
        if (!isApiRequest || !this.isReLoginCandidate(request, errorResponse)) {
          throw errorResponse;
        }
        if (!(await this.reLoginService.awaitReLogin())) {
          throw errorResponse;
        }
        // One retry. A second 401 reaches the caller like any other error.
        return await lastValueFrom(next.handle(request));
      }
    };

    return from(
      handleValidation()
        .then((response: any) => {
          if (request.responseType === 'json') {
            const { body } = response;
            this.recursivelyParseDateFromJsonResponseBody(body);
          }

          if (!RequestContextProperties.has('SkipLoadingMask')) {
            this.loadingService.hide();
          }

          return response;
        })
        .catch((errorResponse) => {
          let { error, message, status } = errorResponse;

          if (!RequestContextProperties.has('SkipLoadingMask')) {
            this.loadingService.hide();
          }

          if (error instanceof ArrayBuffer) {
            error = this.decodeArrayBufferError(error);
            Object.assign(errorResponse, { error });
          }

          // Callers that opt out of interceptor toasts must stay silent even
          // when the failure is network-level. An SSO password refusal is
          // reported by each caller in its own words: it reads differently on
          // a sign-in, a reset and the user screen.
          const manuallyHandled =
            RequestContextProperties.has('ManuallyHandleError') ||
            [
              'ManuallyHandledError',
              'TermsAndConditionsError',
              SSO_SIGN_IN_REQUIRED_ERROR_NAME
            ].includes(error?.name);

          // Status 0 is a network-level failure (drop, timeout, abort).
          if (manuallyHandled) {
            // The caller reports this one itself.
          } else if (status === 0 || new Set([502, 504]).has(status)) {
            this.ToastService.addToast({
              header: 'Error',
              body: 'Something went wrong on our end. Try refreshing the page.',
              autohide: true,
              delay: 4000,
              class: 'toast-error'
            } as Toast);
          } else if (error) {
            this.ToastService.addToast({
              header: 'Error',
              body: this.buildErrorToastBody(error),
              autohide: true,
              delay: 4000,
              class: 'toast-error'
            } as Toast);
          }

          return Promise.reject(errorResponse);
        })
    );
  }

  /**
   * A 401 from an ordinary API call means the session lapsed. Not for the four
   * credential endpoints (a 401 there is a refused credential the caller
   * reports), the signed-out password-reset and public routes, or while the
   * login page is already showing. The other /auth routes, the impersonation
   * endpoints included, carry the session like any API call, so their 401 is a
   * lapsed session too.
   */
  private isReLoginCandidate(
    request: HttpRequest<unknown>,
    errorResponse: unknown
  ): boolean {
    if (
      !(errorResponse instanceof HttpErrorResponse) ||
      errorResponse.status !== 401
    ) {
      return false;
    }
    const { url } = request;
    // `session` is anchored so `/auth/session/impersonation` does not match.
    const isCredentialRoute =
      /\/auth\/(session(\?|$)|session\/exchange|token|refresh)/.test(url);
    const isPublicRoute = /\/public\//.test(url);
    const isPasswordResetRoute = /\/api\/v3\/users\/password-reset(\/|$)/.test(
      url
    );
    const isOnLoginPage = window.location.pathname.startsWith('/login');
    return (
      !isCredentialRoute &&
      !isPublicRoute &&
      !isPasswordResetRoute &&
      !isOnLoginPage
    );
  }
}
